/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import {
  UserRole,
  UserGroup,
  AccessRule,
  GROUP_DEFINITIONS,
  ACCESS_RULES,
  type AccessReference,
  type AsyncAccessOptions,
  type IAccessService,
  type LegacyAccessAction,
  type FeaturesInfo,
  type AppStatus
} from '../types/access'
import { getUser, getUserRoles } from './auth'
import { toast } from './toast'

/**
 * AccessService - React TypeScript equivalent of AngularJS AccessService
 *
 * Provides role-based access control functionality including:
 * - Permission checking for users and groups
 * - Async access validation with redirect handling
 * - QC access control
 * - Legacy action object support
 */
class AccessService implements IAccessService {
  private static instance: AccessService

  public static getInstance(): AccessService {
    if (!AccessService.instance) {
      AccessService.instance = new AccessService()
    }
    return AccessService.instance
  }

  /**
   * Check if user is allowed access to a resource
   * @param ref - Access reference (AccessRule, UserRole array, or legacy action)
   * @returns true if user has access, false otherwise
   */
  public isAllowed(ref: AccessReference): boolean {
    if (Array.isArray(ref)) {
      // Direct array of roles
      return this.areRolesPermitted(ref)
    } else if (typeof ref === 'string') {
      // Check if it's an AccessRule key
      if (this.isAccessRule(ref)) {
        const roles = this.getRolesForAccessRule(ref as AccessRule)
        return this.areRolesPermitted(this.expandGroups(roles))
      } else {
        // Legacy action object support
        return this.processLegacyAction(ref)
      }
    } else {
      // Legacy action object
      return this.processLegacyAction(ref as LegacyAccessAction)
    }
  }

  /**
   * Check if user is NOT allowed access to a resource
   * @param ref - Access reference
   * @returns true if user does NOT have access, false otherwise
   */
  public isNotAllowed(ref: AccessReference): boolean {
    return !this.isAllowed(ref)
  }

  /**
   * Async permission check with automatic redirect on failure
   * @param ref - Access reference
   * @param options - Async access options
   * @returns Promise that resolves if access granted, rejects if denied
   */
  public async isAllowedStateAsync(ref: AccessReference, options: AsyncAccessOptions = {}): Promise<boolean> {
    return this.processAccessAsync(ref, true, options)
  }

  /**
   * Async permission check for NOT allowed with automatic redirect on success
   * @param ref - Access reference
   * @param options - Async access options
   * @returns Promise that resolves if access denied, rejects if granted
   */
  public async isNotAllowedAsync(ref: AccessReference, options: AsyncAccessOptions = {}): Promise<boolean> {
    return this.processAccessAsync(ref, false, options)
  }

  /**
   * Check if QC (Quality Control) access is allowed
   * @returns Promise that resolves if QC access is enabled, rejects otherwise
   */
  public async isQCAccessAllowedAsync(): Promise<void> {
    try {
      // Check if features info is available first
      const featuresInfo = await this.getFeaturesInfo()

      if (featuresInfo?.qcReview) {
        if (featuresInfo.qcReview.qcReviewEnabled) {
          return Promise.resolve()
        } else {
          return Promise.reject(new Error('QC Review is disabled'))
        }
      } else {
        // Fallback to app status check
        const appStatus = await this.getAppStatus()
        if (appStatus.qcReviewEnabled) {
          return Promise.resolve()
        } else {
          return Promise.reject(new Error('QC Review is disabled'))
        }
      }
    } catch (error) {
      return Promise.reject(error)
    }
  }

  /**
   * Check if a condition passes
   * @param condition - Boolean condition to check
   * @returns Promise that resolves if condition is true, rejects if false
   */
  public async passesCondition(condition: boolean): Promise<void> {
    if (condition) {
      return Promise.resolve()
    } else {
      return Promise.reject(new Error('Condition not met'))
    }
  }

  /**
   * Check if user roles are permitted for given allowed roles
   * @param allowedRoles - Array of roles that are allowed
   * @returns true if user has any of the allowed roles
   */
  public areRolesPermitted(allowedRoles: UserRole[]): boolean {
    if (!Array.isArray(allowedRoles)) {
      console.warn('allowedRoles needs to be an array')
      return false
    }

    const assignedRoles = getUserRoles().filter((role): role is UserRole => role !== null)
    const flatRoles = allowedRoles.flat()
    return assignedRoles.some((role) => flatRoles.includes(role))
  }

  /**
   * Check user access against required roles/groups
   * @param userRoles - User's assigned roles
   * @param requiredRoles - Required roles or groups for access
   * @returns true if user has access
   */
  public checkUserAccess(userRoles: UserRole[], requiredRoles: (UserRole | UserGroup)[]): boolean {
    const expandedRoles = this.expandGroups(requiredRoles)
    return userRoles.some((role) => expandedRoles.includes(role))
  }

  /**
   * Expand groups to individual roles
   * @param entities - Array of roles and/or groups
   * @returns Array of individual roles
   */
  private expandGroups(entities: (UserRole | UserGroup)[]): UserRole[] {
    const expandedRoles: UserRole[] = []

    entities.forEach((entity) => {
      if (this.isUserGroup(entity)) {
        const groupRoles = GROUP_DEFINITIONS[entity as UserGroup]
        if (groupRoles) {
          expandedRoles.push(...groupRoles)
        }
      } else {
        expandedRoles.push(entity as UserRole)
      }
    })

    return Array.from(new Set(expandedRoles)) // Remove duplicates
  }

  /**
   * Check if entity is a user group
   * @param entity - Entity to check
   * @returns true if entity is a UserGroup
   */
  private isUserGroup(entity: UserRole | UserGroup): boolean {
    return Object.values(UserGroup).includes(entity as UserGroup)
  }

  /**
   * Check if string is a valid AccessRule
   * @param rule - String to check
   * @returns true if valid AccessRule
   */
  private isAccessRule(rule: string): boolean {
    return Object.values(AccessRule).includes(rule as AccessRule)
  }

  /**
   * Get roles for an access rule
   * @param rule - AccessRule to get roles for
   * @returns Array of roles and groups
   */
  private getRolesForAccessRule(rule: AccessRule): (UserRole | UserGroup)[] {
    return ACCESS_RULES[rule] || []
  }

  /**
   * Process legacy action object
   * @param action - Legacy action object or string
   * @returns true if access allowed
   */
  private processLegacyAction(action: LegacyAccessAction | string): boolean {
    if (typeof action === 'string') {
      // Handle availableRoles string format
      if (action.includes(',')) {
        const roles = action.split(',').map((role) => role.trim() as UserRole)
        return this.areRolesPermitted(roles)
      }
      return false
    }

    if (action.availableRoles) {
      const roles = action.availableRoles.split(',').map((role) => role.trim() as UserRole)
      return this.areRolesPermitted(roles)
    }

    return false
  }

  /**
   * Process async access check with redirect handling
   * @param ref - Access reference
   * @param isAllowed - Whether to check for allowed (true) or not allowed (false)
   * @param options - Async options
   * @returns Promise resolving to boolean
   */
  private async processAccessAsync(
    ref: AccessReference,
    isAllowed: boolean,
    options: AsyncAccessOptions = {}
  ): Promise<boolean> {
    return new Promise((resolve, reject) => {
      // Wait for user info to be available
      const checkAccess = () => {
        const user = getUser()
        if (!user) {
          // Retry after a short delay
          setTimeout(checkAccess, 10)
          return
        }

        const hasAccess = this.isAllowed(ref)
        const accessGranted = isAllowed ? hasAccess : !hasAccess

        if (accessGranted) {
          resolve(true)
          return
        }

        // Handle access denied
        if (!options.hideDontAccessErrorMsg) {
          const errorMessage = options.customErrorMessage || 'ERROR_NOT_ALLOWED_OPERATION'
          toast.error(errorMessage)
        }

        // Legacy behavior: ALWAYS redirect on access failure unless explicitly disabled
        // Use nullish coalescing to default to true (matching legacy behavior)
        if (options.redirectOnFailure ?? true) {
          // Redirect to root (which will redirect to appropriate page based on role)
          setTimeout(() => {
            window.location.href = '/'
          }, 100)
        }

        reject(false)
      }

      checkAccess()
    })
  }

  /**
   * Get features info (placeholder - would integrate with actual features service)
   * @returns Promise resolving to features info
   */
  private async getFeaturesInfo(): Promise<FeaturesInfo | null> {
    // This would integrate with the actual features/init service
    // For now, return null to trigger fallback to app status
    return Promise.resolve(null)
  }

  /**
   * Get app status (placeholder - would integrate with actual API)
   * @returns Promise resolving to app status
   */
  private async getAppStatus(): Promise<AppStatus> {
    // This would integrate with the actual API service
    // For now, return default status
    return Promise.resolve({ qcReviewEnabled: false })
  }
}

// Export singleton instance
export const accessService = AccessService.getInstance()
export default accessService
